import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Alert, Paper, Chip, InputAdornment, Accordion, AccordionSummary, AccordionDetails, MenuItem,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import EventIcon from '@mui/icons-material/Event';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { ProjectConfig } from 'Global';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  ADMIN_WHATSAPP, RANGO_HORARIO_LABELS, CAMPOS_ESPECIFICOS_LABELS,
  PROCEDIMIENTO_LABELS, COMISION_POR_PROCEDIMIENTO, RANGOS_HORARIOS_OPTIONS,
} from './quotesConfig';
import { formatRut } from './rutUtils';

type ViewState = 'validate' | 'form' | 'success' | 'taken';

export default function NurseQuoteView() {
  const { token: request_id } = useParams<{ token: string }>();
  const [rut, setRutValue] = useState('');
  const [viewState, setViewState] = useState<ViewState>('validate');
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState<any>(null);
  const [hadPreviousOffer, setHadPreviousOffer] = useState(false);
  const [insumosText, setInsumosText] = useState('');
  const [precio, setPrecio] = useState('');
  const [disponibilidadHorario, setDisponibilidadHorario] = useState('');
  const [disponibilidadCustom, setDisponibilidadCustom] = useState('');
  const [notaPersonalizada, setNotaPersonalizada] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const setRut = (e: any) => setRutValue(formatRut(e.target.value));

  const handleValidate = async () => {
    if (!rut.trim()) { setErrorMsg('Ingresa tu RUT'); return; }
    setValidating(true);
    setErrorMsg('');
    try {
      const resp = await axios.get(`${ProjectConfig.api_url}/quotes/nurse-view`, {
        params: { request_id, rut: rut.trim() },
      });
      const d = resp.data;
      if (d.status === 'rut_not_found') {
        setErrorMsg(d.message);
      } else if (d.status === 'taken') {
        setViewState('taken');
      } else if (d.status === 'success') {
        setData(d);
        if (d.my_offer) {
          setPrecio(d.my_offer.precio || '');
          setInsumosText(d.my_offer.insumos_text || '');
          setNotaPersonalizada(d.my_offer.nota_personalizada || '');
          const savedHorario = d.my_offer.disponibilidad_horario || '';
          const isFixedOption = RANGOS_HORARIOS_OPTIONS.some(o => o.value === savedHorario && o.value !== 'personalizado');
          if (savedHorario && !isFixedOption) {
            setDisponibilidadHorario('personalizado');
            setDisponibilidadCustom(savedHorario);
          } else {
            setDisponibilidadHorario(savedHorario);
          }
          setHadPreviousOffer(true);
        }
        setViewState('form');
      } else {
        setErrorMsg(d.message || 'Error al cargar la solicitud');
      }
    } catch {
      setErrorMsg('Error de conexión. Intenta nuevamente.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmitOffer = async () => {
    if (!insumosText.trim()) { setErrorMsg('Describe los insumos que vas a utilizar'); return; }
    if (!precio) { setErrorMsg('Ingresa el precio del servicio'); return; }
    if (!disponibilidadHorario) { setErrorMsg('Indica tu horario de disponibilidad'); return; }
    if (disponibilidadHorario === 'personalizado' && !disponibilidadCustom.trim()) {
      setErrorMsg('Especifica tu horario de disponibilidad'); return;
    }
    const disponibilidadFinal = disponibilidadHorario === 'personalizado' ? disponibilidadCustom.trim() : disponibilidadHorario;

    setSubmitting(true);
    setErrorMsg('');
    try {
      const resp = await axios.post(`${ProjectConfig.api_url}/quotes/nurse-submit-offer`, {
        request_id,
        nurse_user_id: data.nurse.user_id,
        nurse_rut: rut,
        precio,
        insumos_text: insumosText,
        disponibilidad_horario: disponibilidadFinal,
        nota_personalizada: notaPersonalizada,
      });
      if (resp.data.status === 'success') {
        setImageUrl(resp.data.image_url);
        setViewState('success');

        const req = resp.data.request;
        const procedimientoLabel = PROCEDIMIENTO_LABELS[req.procedimiento] || (req.procedimiento || '').replace(/_/g, ' ');
        const msg = encodeURIComponent(
          `Enfermera ${resp.data.nurse_name} cotiza el procedimiento "${procedimientoLabel}" para ${req.nombre} ${req.apellido}. Cotización: ${resp.data.image_url}\n\nRecuerda presionar Enviar para mandar la cotización.`
        );
        window.open(`https://wa.me/${ADMIN_WHATSAPP}?text=${msg}`, '_blank');
      } else if (resp.data.status === 'taken') {
        setViewState('taken');
      } else {
        setErrorMsg(resp.data.message || 'Error al enviar la cotización');
      }
    } catch {
      setErrorMsg('Error de conexión. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f5fb', py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" fontWeight={800} color="#125095">
            Especialista en Heridas
          </Typography>
          <Typography color="text.secondary" fontSize={14}>
            Portal de solicitudes para enfermeras
          </Typography>
        </Box>

        {/* STEP 1: RUT validation */}
        {viewState === 'validate' && (
          <Paper elevation={0} sx={{ borderRadius: 3, p: 4, border: '1px solid #dde6f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <LockIcon sx={{ color: '#125095' }} />
              <Typography fontWeight={700} fontSize={18}>Validación de identidad</Typography>
            </Box>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Para acceder a la solicitud del paciente, ingresa tu RUT tal como aparece en tu perfil de Especialista en Heridas.
            </Typography>
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMsg}
                {errorMsg.includes('registrado') && (
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" href="/cuenta/editar" sx={{ color: '#125095' }}>
                      Ir a completar mi RUT
                    </Button>
                  </Box>
                )}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Tu RUT"
              placeholder="12.345.678-9"
              value={rut}
              onChange={setRut}
              inputProps={{ maxLength: 12 }}
              onKeyDown={e => e.key === 'Enter' && handleValidate()}
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleValidate}
              disabled={validating}
              sx={{ bgcolor: '#125095', borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#0e3d78' } }}
            >
              {validating ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Acceder a la solicitud'}
            </Button>
          </Paper>
        )}

        {/* TAKEN: request was closed by the admin, or another nurse already took it */}
        {viewState === 'taken' && (
          <Paper elevation={0} sx={{ borderRadius: 3, p: 4, border: '1px solid #dde6f5', textAlign: 'center' }}>
            <LockIcon sx={{ fontSize: 48, color: '#9c6b2e', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Esta solicitud ya fue tomada
            </Typography>
            <Typography color="text.secondary">
              El paciente ya se decidió por otra enfermera. Gracias por tu disposición — te avisaremos ante una próxima solicitud en tu comuna.
            </Typography>
          </Paper>
        )}

        {/* STEP 2: patient data + offer form */}
        {viewState === 'form' && data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid #dde6f5' }}>
              <Typography fontWeight={700} fontSize={16} color="#125095" gutterBottom>
                Datos del paciente
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" sx={{ color: '#888' }} />
                  <Typography>{data.request.nombre} {data.request.apellido}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon fontSize="small" sx={{ color: '#888' }} />
                  <Typography>{data.request.direccion || 'Sin dirección'} — {data.request.comuna_name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalServicesIcon fontSize="small" sx={{ color: '#888' }} />
                  <Chip label={PROCEDIMIENTO_LABELS[data.request.procedimiento] || data.request.procedimiento?.replace(/_/g, ' ')} size="small" sx={{ bgcolor: '#e4eef9', color: '#125095' }} />
                </Box>
                {Object.keys(data.request.campos_especificos || {}).length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 3.5 }}>
                    {Object.entries(data.request.campos_especificos).map(([key, value]) => (
                      <Chip
                        key={key}
                        label={`${CAMPOS_ESPECIFICOS_LABELS[key] || key}: ${value}`}
                        size="small"
                        sx={{ bgcolor: '#f4f7fb', color: '#475569', fontSize: 12 }}
                      />
                    ))}
                  </Box>
                )}
                {data.request.fecha_deseada && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon fontSize="small" sx={{ color: '#888' }} />
                    <Typography>Fecha deseada: {new Date(data.request.fecha_deseada).toLocaleDateString('es-CL')}</Typography>
                  </Box>
                )}
                {data.request.rango_horario && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" sx={{ color: '#888' }} />
                    <Typography>Horario disponible: {RANGO_HORARIO_LABELS[data.request.rango_horario] || data.request.rango_horario}</Typography>
                  </Box>
                )}
                {data.request.descripcion && (
                  <Typography color="text.secondary" fontSize={14} sx={{ mt: 0.5 }}>
                    {data.request.descripcion}
                  </Typography>
                )}
              </Box>
            </Paper>

            {data.files?.length > 0 && (
              <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid #dde6f5' }}>
                <Typography fontWeight={700} fontSize={16} color="#125095" gutterBottom>
                  Fotografías de la herida
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {data.files.map((f: any, i: number) => (
                    <Box
                      key={i}
                      component="a"
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ display: 'block', borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}
                    >
                      <img
                        src={f.url}
                        alt={`herida-${i + 1}`}
                        style={{ width: 120, height: 120, objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid #dde6f5' }}>
              <Typography fontWeight={700} fontSize={16} color="#125095" gutterBottom>
                Tu cotización — {data.nurse.name}
              </Typography>
              {hadPreviousOffer && (
                <Alert severity="info" sx={{ mb: 2 }}>Ya habías cotizado esta solicitud. Puedes actualizar tu precio o insumos y volver a enviarla.</Alert>
              )}
              {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

              <Box
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.5,
                  bgcolor: '#FFF8EC', border: '1px solid #F3D9A6', borderLeft: '4px solid #C97A3D',
                  borderRadius: 2, p: 2, mb: 2,
                }}
              >
                <WarningAmberIcon sx={{ color: '#C97A3D', mt: '2px' }} fontSize="small" />
                <Box sx={{ flex: 1 }}>
                  <Typography fontSize={14} fontWeight={700} color="#7A4A1E">
                    No olvides incluir el valor de la comisión en tu cotización para el paciente.
                  </Typography>
                  {COMISION_POR_PROCEDIMIENTO[data.request.procedimiento] ? (
                    <Typography fontSize={13.5} color="#7A4A1E" sx={{ mt: .5 }}>
                      Comisión de la plataforma para <b>{PROCEDIMIENTO_LABELS[data.request.procedimiento] || data.request.procedimiento}</b>:{' '}
                      <b>${COMISION_POR_PROCEDIMIENTO[data.request.procedimiento].toLocaleString('es-CL')}</b> — súmala al precio que le mostrarás al paciente.
                    </Typography>
                  ) : (
                    <Typography fontSize={13.5} color="#7A4A1E" sx={{ mt: .5 }}>
                      Este procedimiento no tiene una comisión fija — consulta el valor con administración antes de enviar tu cotización.
                    </Typography>
                  )}

                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{ bgcolor: 'transparent', mt: 1, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: '#C97A3D' }} />}
                      sx={{ p: 0, minHeight: 'auto', '& .MuiAccordionSummary-content': { m: 0, my: 0.5 } }}
                    >
                      <Typography fontSize={12.5} color="#C97A3D" fontWeight={600}>Ver tabla completa de comisiones</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pt: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: .5 }}>
                        {Object.entries(COMISION_POR_PROCEDIMIENTO).map(([key, value]) => (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#7A4A1E' }}>
                            <span>{PROCEDIMIENTO_LABELS[key] || key}</span>
                            <b>${value.toLocaleString('es-CL')}</b>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Precio del servicio (CLP) *"
                type="number"
                value={precio}
                onChange={e => setPrecio(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />

              <TextField
                fullWidth
                label="Descripción de tu atención *"
                multiline
                rows={5}
                value={insumosText}
                onChange={e => setInsumosText(e.target.value)}
                placeholder="Describe qué incluye tu atención (insumos básicos o avanzados, evaluación, educación), si el paciente debe aportar algún material y destaca brevemente tu experiencia o certificación clínica."
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                select
                label="Tu horario de disponibilidad *"
                value={disponibilidadHorario}
                onChange={e => setDisponibilidadHorario(e.target.value)}
                sx={{ mb: 2 }}
              >
                {RANGOS_HORARIOS_OPTIONS.map(r => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>

              {disponibilidadHorario === 'personalizado' && (
                <TextField
                  fullWidth
                  label="Especifica tu horario de disponibilidad *"
                  value={disponibilidadCustom}
                  onChange={e => setDisponibilidadCustom(e.target.value)}
                  placeholder="Ej: entre las 15:00 y las 18:00"
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                fullWidth
                label="Nota adicional para el paciente (opcional)"
                multiline
                rows={2}
                value={notaPersonalizada}
                onChange={e => setNotaPersonalizada(e.target.value)}
                placeholder="Cualquier otro detalle que quieras agregar a tu cotización."
                sx={{ mb: 2 }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Esta solicitud queda abierta para que otras enfermeras también coticen — el paciente decide según precio. Al enviar, se genera una imagen de la cotización y se avisa al administrador por WhatsApp.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmitOffer}
                disabled={submitting}
                sx={{ bgcolor: '#125095', borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#0e3d78' } }}
              >
                {submitting
                  ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                  : hadPreviousOffer ? 'Actualizar cotización' : 'Enviar cotización'}
              </Button>
            </Paper>
          </Box>
        )}

        {/* SUCCESS */}
        {viewState === 'success' && (
          <Paper elevation={0} sx={{ borderRadius: 3, p: 5, border: '1px solid #dde6f5', textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 2 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>Cotización enviada</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Se generó la imagen de la cotización y se notificó al administrador. Puedes volver a este link más adelante si quieres actualizar tu precio.
            </Typography>
            {imageUrl && (
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                href={imageUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ borderColor: '#125095', color: '#125095', borderRadius: 2 }}
              >
                Ver cotización
              </Button>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
